import { WSPageToolbarAction } from "@wingspanhq/fe-component-library";
import { IRequirementResponse } from "@wingspanhq/payments/dist/interfaces";
import { IIdentityVerificationRequestResponse } from "@wingspanhq/users";
import { IdentityVerificationRequestStatus } from "@wingspanhq/users/dist/lib/interfaces";
import startCase from "lodash/startCase";
import {
  DataItemProps,
  DataView
} from "../../../../../../shared/components/DataView";
import { getRequirementDefinitionTableCellProps } from "../../../../../RequirementDefinitions/utils";
import {
  CellsConfig,
  ContextItem,
  RequirementContextType,
  RequirementTableCallback
} from "../../../../types";
import { IdentityVerificationDetailsWidgetsContainer } from "../../components/IdentityVerificationDetailsWidgetsContainer";
import { getIdentityVerificationPayeeProgressSteps } from "../helpers/getIdentityVerificationPayeeProgressSteps";
import { getIdentityVerificationPayerRequirementStatus } from "../helpers/getIdentityVerificationPayerRequirementStatus";
import { getIdentityVerificationRequirementName } from "../helpers/getIdentityVerificationRequirementName";
import { payeeIdentityVerificationRenderCardDescription } from "./payeeIdentityVerificationRenderCardDescription";

export const usePayeeIdentityVerificationConfig = (
  callback: RequirementTableCallback
): CellsConfig<
  IRequirementResponse,
  ContextItem,
  IIdentityVerificationRequestResponse | undefined
> => {
  return {
    isActionRequired: requirement => {
      return false;
    },
    requirementDetailsProgressWidget: (
      requirement,
      context,
      identityVerificationRequest
    ) => {
      return identityVerificationRequest?.status
        ? getIdentityVerificationPayeeProgressSteps(
            identityVerificationRequest.status
          )
        : undefined;
    },
    requirementDetailsWidgets: (
      requirement,
      context,
      identityVerificationRequest
    ) => {
      return identityVerificationRequest ? (
        <IdentityVerificationDetailsWidgetsContainer
          identityVerificationRequest={identityVerificationRequest}
          contextType={RequirementContextType.Payee}
          requirement={requirement}
          onSuccess={callback}
          isInitialLoading={false}
        />
      ) : null;
    },
    requirementStatusAlert: (
      requirement,
      context,
      identityVerificationRequest
    ) => {
      if (
        identityVerificationRequest?.status ===
        IdentityVerificationRequestStatus.Pending
      ) {
        return {
          theme: "info",
          title: "Awaiting contractor",
          icon: "time",
          children: "Awaiting contractor to complete the requirement."
        };
      }
    },
    requirementName: getIdentityVerificationRequirementName,
    requirementDetails: (requirement, context, identityVerificationRequest) => {
      return {
        header: {
          label: {
            text: "Identity verification"
          },
          value: {
            pill:
              identityVerificationRequest?.status ===
              IdentityVerificationRequestStatus.Failed
                ? {
                    theme: "error",
                    text: "Failed",
                    icon: true
                  }
                : undefined
          }
        },
        children: payeeIdentityVerificationRenderCardDescription(
          requirement,
          identityVerificationRequest
        ),
        data: [],
        actions: undefined
      };
    },
    requirementAdditionalContent: (
      requirement,
      context,
      identityVerificationRequest
    ) => {
      const extractedFields =
        identityVerificationRequest?.identityVerification?.extractedFields;

      if (extractedFields && Object.keys(extractedFields).length) {
        const mapIdentityVerificationExtractedFieldsToDataView = (
          extractedFields: Record<string, string>
        ): DataItemProps[] => {
          return Object.entries(extractedFields).map(([key, value]) => {
            const keyAsLabel = startCase(key).toLowerCase();
            // make uppercase the first letter of the key
            const label =
              keyAsLabel.charAt(0).toUpperCase() + keyAsLabel.slice(1);

            return {
              label,
              value
            };
          });
        };

        const fields = mapIdentityVerificationExtractedFieldsToDataView(
          extractedFields!
        );

        return <DataView title="Document details" data={fields} />;
      }
    },
    toolbarActions: (requirement, context, identityVerificationRequest) => {
      const actions: WSPageToolbarAction[] = [];

      return actions;
    },
    nameCell: (requirement, context, identityVerificationRequest) => {
      return {
        text: getIdentityVerificationRequirementName(
          requirement,
          context,
          identityVerificationRequest
        )
      };
    },
    typeCell: requirement =>
      getRequirementDefinitionTableCellProps({
        type: requirement.requirementType
      }),
    statusCell: (requirement, context, identityVerificationRequest) => {
      return getIdentityVerificationPayerRequirementStatus(
        requirement,
        context,
        identityVerificationRequest
      );
    }
  };
};
